import { createSlice } from "@reduxjs/toolkit";
import {
  changePassword,
  editAdminProfileImage,
  editProfileDetails,
  forgotPwSubmit,
  getUser,
  loginVerificationResend,
  resetPasswordSubmit,
  signInSubmit,
  signOutSubmit,
  signUpSubmit,
  verificationLogin,
  verificationSubmit,
} from "./authService";

const initialState = {

  signUpDetails: {
    isSignUpLoading: null,
    isSignedUp: null,
    signUpMessage: null,
  },

  signInDetails: {
    isSignInLoading: null,
    isSignedIn: null,
    signInMessage: null,
  },

  verifyDetails: {
    isVerificationLoading: null,
    isVerified: null,
    verificationMsg: null,
  },

  verifyLoginDetails: {
    isLoginVerificationLoading: null,
    isLoginVerified: null,
    verificationLoginMsg: null,
  },

  resendLoginCode: {
    isResendLoginCodeLoading: null,
    isResentLoginCode: null,
    resendLoginCodeMsg: null,
  },

  userDetails: {
    isUserDetailsLoading: null,
    isUserDetails: null,
    userDetailsMessage: null,
    roleName: null,
    isLoggedIn: false,
    isLoading: false,
    hasCheckedAuth: false,
    details: {
      id: null,
      email: null,
      full_name: null,
      image_url: null,
      phone_number: null,
    },
  },

  adminImageUpdate: {
    imageUpdateStatus: null,
    message: null,
  },

  adminChangePassword: {
    status: null,
    message: null,
    isChangingPassword: null
  },

  profileDetailsUpdate: {
    isDetailsLoading: null,
    detailsSuccess: null,
    detailsMessage: null
  },

  sendEmail: {
    isEmailSending: null,
    isEmailSent: null,
    emailSendMsg: null,
  },

  resetPassword: {
    isResetPwLoading: null,
    isResetPwSuccess: null,
    resetPwMsg: null,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // 🔐 SignUp
      .addCase(signUpSubmit.pending, (state) => {
        state.signUpDetails = {
          ...initialState.signUpDetails,
          isSignUpLoading: true,
          signUpMessage: "Loading SignUp....",
        };
      })
      .addCase(signUpSubmit.rejected, (state, action) => {
        state.signUpDetails = {
          ...initialState.signUpDetails,
          isSignUpLoading: false,
          isSignedUp: false,
          signUpMessage: action.payload?.message || "Signup failed.",
        };
      })
      .addCase(signUpSubmit.fulfilled, (state, action) => {
        state.signUpDetails = {
          isSignUpLoading: false,
          isSignedUp: true,
          signUpMessage: action.payload.message,
        };
      });

    builder
      .addCase(verificationSubmit.pending, (state) => {
        state.verifyDetails = {
          ...initialState.verifyDetails,
          isVerificationLoading: true,
          verificationMsg: "Loading...",
        };
      })
      .addCase(verificationSubmit.rejected, (state, action) => {
        state.verifyDetails = {
          ...initialState.verifyDetails,
          isVerificationLoading: false,
          isVerified: false,
          verificationMsg: action.payload,
        };
      })
      .addCase(verificationSubmit.fulfilled, (state, action) => {
        state.verifyDetails = {
          ...initialState.verifyDetails,
          isVerificationLoading: false,
          isVerified: true,
          verificationMsg: action.payload,
        };
      });

    // 🔐 SignIn
    builder
      .addCase(signInSubmit.pending, (state) => {
        state.signInDetails = {
          ...initialState.signInDetails,
          isSignInLoading: true,
          signInMessage: "Loading...",
        };
      })
      .addCase(signInSubmit.rejected, (state, action) => {
        state.signInDetails = {
          ...initialState.signInDetails,
          isSignInLoading: false,
          isSignedIn: false,
          signInMessage: action.payload,
        };
      })
      .addCase(signInSubmit.fulfilled, (state, action) => {
        state.signInDetails = {
          ...initialState.signInDetails,
          isSignInLoading: false,
          isSignedIn: true,
          signInMessage: action.payload,
        };
      });

    builder
      .addCase(verificationLogin.pending, (state) => {
        state.verifyLoginDetails = {
          ...initialState.verifyLoginDetails,
          isLoginVerificationLoading: true,
          verificationLoginMsg: "Loading...",
        };
      })
      .addCase(verificationLogin.rejected, (state, action) => {
        state.verifyLoginDetails = {
          ...initialState.verifyLoginDetails,
          isLoginVerificationLoading: false,
          isLoginVerified: false,
          verificationLoginMsg: action.payload,
        };
      })
      .addCase(verificationLogin.fulfilled, (state, action) => {
        state.verifyLoginDetails = {
          ...initialState.verificationLogin,
          isLoginVerificationLoading: false,
          isLoginVerified: true,
          verificationLoginMsg: action.payload,
        };
      });

    //Resend login verify code
    builder
      .addCase(loginVerificationResend.pending, (state) => {
        state.resendLoginCode = {
          ...initialState.resendLoginCode,
          isResendLoginCodeLoading: true,
          resendLoginCodeMsg: 'Loading.....',
        };
      })
      .addCase(loginVerificationResend.rejected, (state, action) => {
        state.resendLoginCode = {
          ...initialState.resendLoginCode,
          isResendLoginCodeLoading: false,
          isResentLoginCode: false,
          resendLoginCodeMsg: action.payload.message,
        };
      })
      .addCase(loginVerificationResend.fulfilled, (state, action) => {
        state.resendLoginCode = {
          ...initialState.resendLoginCode,
          isResendLoginCodeLoading: false,
          isResentLoginCode: true,
          resendLoginCodeMsg: action.payload.message,
        };
      });

    builder
      .addCase(getUser.pending, (state) => {
        state.userDetails = {
          ...initialState.userDetails,
          isUserDetailsLoading: true,
          userDetailsMessage: "Loading",
        };
      })
      .addCase(getUser.rejected, (state, action) => {
        state.userDetails = {
          ...initialState.userDetails,
          isUserDetailsLoading: false,
          userDetailsMessage: action.payload,
          isLoggedIn: false,
          hasCheckedAuth: true,
        };
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.userDetails = {
          ...initialState.userDetails,
          isUserDetailsLoading: false,
          isUserDetails: true,
          userDetailsMessage: "Successfully loaded user details",
          details: action.payload,
          isLoggedIn: action.payload.is_loggedin,
          roleName: action.payload.role?.name,
          hasCheckedAuth: true,
        };
      });

    //Profile update
    builder
      .addCase(editProfileDetails.pending, (state) => {
        state.profileDetailsUpdate = {
          ...initialState.profileDetailsUpdate,
          isDetailsLoading: true,
          detailsMessage: "Loading",
        };
      })
      .addCase(editProfileDetails.rejected, (state, action) => {
        state.profileDetailsUpdate = {
          ...initialState.profileDetailsUpdate,
          isDetailsLoading: false,
          detailsSuccess: false,
          detailsMessage: action.payload,
        };
      })
      .addCase(editProfileDetails.fulfilled, (state, action) => {
        state.profileDetailsUpdate = {
          ...initialState.profileDetailsUpdate,
          isDetailsLoading: false,
          detailsSuccess: true,
          detailsMessage: "Successfully updated user details",
        };
      });

    builder.addCase(signOutSubmit.pending, (state) => {
      state.userDetails.isLoading = true;
      state.userDetails.message = "Logging out...";
    });
    builder.addCase(signOutSubmit.fulfilled, (state) => {
      localStorage.removeItem("token");
      state.token = null;
      state.userDetails = {
        ...initialState.userDetails,
        isLoggedIn: false,
        hasCheckedAuth: true,
        userDetailsMessage: "Logout successful",
      };
    });
    builder.addCase(signOutSubmit.rejected, (state, action) => {
      state.userDetails.isLoading = false;
      state.userDetails.message =
        action.payload?.message || "Logout failed.";
    });

    builder.addCase(editAdminProfileImage.pending, (state) => {
      state.adminImageUpdate.imageUpdateStatus = "loading";
      state.adminImageUpdate.message = null;
    });
    builder.addCase(editAdminProfileImage.fulfilled, (state, action) => {
      state.adminImageUpdate.imageUpdateStatus = "success";
      state.adminImageUpdate.message = null;
    });
    builder.addCase(editAdminProfileImage.rejected, (state, action) => {
      state.adminImageUpdate.imageUpdateStatus = "failed";
      state.adminImageUpdate.message =
        action.payload || "Failed to update image";
    });

    // Change password
    builder.addCase(changePassword.pending, (state) => {
      state.adminChangePassword.status = 'loading';
      state.adminChangePassword.message = null;
      state.adminChangePassword.isChangingPassword = true;
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      state.adminChangePassword.status = 'success';
      state.adminChangePassword.message = null;
      state.adminChangePassword.isChangingPassword = false;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      state.adminChangePassword.status = 'failed';
      state.adminChangePassword.message = action.payload || 'Failed to update password';
      state.adminChangePassword.isChangingPassword = false;
    });

    // Forgot password
    builder
      .addCase(forgotPwSubmit.pending, (state) => {
        state.sendEmail = {
          ...initialState.sendEmail,
          isEmailSending: true,
          emailSendMsg: 'Sending the email',
        };
      })
      .addCase(forgotPwSubmit.rejected, (state, action) => {
        state.sendEmail = {
          ...initialState.sendEmail,
          isEmailSending: false,
          isEmailSent: false,
          emailSendMsg: action.payload,
        };
      })
      .addCase(forgotPwSubmit.fulfilled, (state, action) => {
        state.sendEmail = {
          ...initialState.sendEmail,
          isEmailSending: false,
          isEmailSent: true,
          emailSendMsg: action.payload,
        };
      });

      builder
      .addCase(resetPasswordSubmit.pending, (state) => {
        state.resetPassword = {
          ...initialState.resetPassword,
          isResetPwLoading: true,
          resetPwMsg: 'Reseting the password',
        };
      })
      .addCase(resetPasswordSubmit.rejected, (state, action) => {
        state.resetPassword = {
          ...initialState.resetPassword,
          isResetPwLoading: false,
          isResetPwSuccess: false,
          resetPwMsg: action.payload,
        };
      })
      .addCase(resetPasswordSubmit.fulfilled, (state, action) => {
        state.resetPassword = {
          ...initialState.resetPassword,
          isResetPwLoading: false,
          isResetPwSuccess: true,
          resetPwMsg: action.payload,
        };
      });
  },
}); 

export const { logout } = authSlice.actions;

export const selectUserDetails = (state) => state.auth.userDetails;
export const selectAuthToken = (state) => state.auth.token;
export const editAdminImage = (state) => state.auth.adminImageUpdate;

export default authSlice.reducer;